import React, { useMemo, useRef } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import PropTypes from "prop-types";

import { ReportAPI } from "@ai360/core";

import { Checkbox, DateInput, Section, SelectInput, SubSection } from "~/core";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { selectors as picklistSelectors, picklistNames } from "~/core/picklist";
import { messages } from "~/reports/i18n-messages";
import { messages as globalMessages } from "~/i18n-messages";
import { model } from "~/reports/data";
import { getReportDataByReportType, getParsedCropData } from "~/reports/data/selectors";
import { useCropList } from "~/admin/setup/customer/use-crop-list";
import {
    IS_SELECTABLE,
    useLoggedInUserProductScopeOrgLevelGuid,
} from "~/admin/setup/customer/use-product-list";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";

import { FieldAPI } from "@ai360/core";
import { useAppSelector } from "~/store/hooks";
import { IOption, ReportOptionsComponent, ReportOptionsComponentProps } from ".";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";

type SkyPlantingSummaryProps = {
    selectedCrop: FieldAPI.ICrop | null;
};

function SkyPlantingSummary({
    hideDefaultOptions,
    intl: { formatMessage },
    onInputChange,
    selectedCrop,
}: ReportOptionsComponentProps & SkyPlantingSummaryProps) {
    const reportData = useAppSelector(getReportDataByReportType(SkyPlantingSummary.reportName));
    const cropScopeOrgLevelGuid = useLoggedInUserProductScopeOrgLevelGuid();
    const { crops: rawCrops } = useCropList({ orgLevelGuid: cropScopeOrgLevelGuid });
    const crops = useMemo(
        () =>
            getParsedCropData(
                rawCrops.map((crop) => ({
                    cropGuid: crop.cropGuid,
                    cropId: String(crop.cropId),
                    cropName: crop.name,
                    activeYn: crop.activeYn,
                    isAgvanceLinked: crop.isAgvanceLinked,
                    ...(crop.isAgvanceLinked === false && {
                        getToolTip: () => formatMessage(globalMessages.cropNotLinkedInAgvance),
                    }),
                }))
            ),
        [rawCrops]
    );
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );

    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);
    const seasonsToDisplay = useRef<string[]>();

    React.useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);

        if (hideDefaultOptions) {
            return;
        }

        if (selectedEventGuidList.size > 0) {
            const seasonGuids = [];

            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (event.agEventTypeName === "Planting") {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }
            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }
            seasonGuids.sort();
            seasonsToDisplay.current = seasonsText;
        } else {
            seasonsToDisplay.current = [];
        }
    }, [selectedEventGuidList.size]);

    const varietyHybridOptions: IOption[] = [
        {
            label: formatMessage(messages.varietyHybrid),
            value: "variety",
        },
        {
            label: formatMessage(messages.monitorVariety),
            value: "monitor",
        },
        {
            label: formatMessage(messages.aliasVarietyHybrid),
            value: "alias",
        },
    ];
    const optionsDisabled = selectedEventGuidList.size > 0;
    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        selectedEventGuidList.size > 0;

    return (
        <Section className="report-options" testId="sky-planting-summary-options-section">
            {!hideDefaultOptions ? (
                <React.Fragment>
                    <SubSection testId="sky-planting-summary-season-and-crop-subsection">
                        <CroppingSeason
                            disabled={croppingSeasonDisabled}
                            required={!croppingSeasonDisabled}
                            reportType={SkyPlantingSummary.reportName}
                            valuesToDisplay={seasonsToDisplay.current}
                        />
                        <SelectInput<FieldAPI.ICrop>
                            disabled={optionsDisabled}
                            optionIsHiddenKey={IS_SELECTABLE}
                            placeholderText={formatMessage(messages.crop)}
                            options={optionsDisabled ? [] : crops}
                            onChange={(value) => {
                                onInputChange(model.PROPS_CROP_GUID, value ? value.cropGuid : null);
                            }}
                            value={optionsDisabled ? null : (selectedCrop as FieldAPI.ICrop)}
                            testId="sky-planting-summary-crop-select"
                        />
                    </SubSection>
                    <SubSection testId="sky-planting-summary-dates-subsection">
                        <DateInput
                            disabled={optionsDisabled}
                            placeholderText={formatMessage(messages.startDate)}
                            onChange={(value) =>
                                onInputChange(model.PROPS_START_DATE, value ? value : null)
                            }
                            timeFormat={false}
                            value={optionsDisabled ? null : reportData[model.PROPS_START_DATE]}
                            testId="sky-planting-summary-start-date-date"
                        />
                        <DateInput
                            disabled={optionsDisabled}
                            placeholderText={formatMessage(messages.endDate)}
                            onChange={(value) =>
                                onInputChange(model.PROPS_END_DATE, value ? value : null)
                            }
                            timeFormat={false}
                            value={optionsDisabled ? null : reportData[model.PROPS_END_DATE]}
                            testId="sky-planting-summary-end-date-date"
                        />
                    </SubSection>
                </React.Fragment>
            ) : null}
            <SubSection
                className="two-report-options"
                testId="sky-planting-summary-variety-hybrid-type-subsection"
            >
                <SelectInput
                    required
                    clearable={false}
                    containerClassNames={["display-options-list"]}
                    placeholderText={formatMessage(messages.varietyHybridType)}
                    options={varietyHybridOptions}
                    onChange={(value) => {
                        onInputChange(model.PROPS_VARIETY_HYBRID_TYPE, value);
                    }}
                    value={reportData[model.PROPS_VARIETY_HYBRID_TYPE]}
                    testId="sky-planting-summary-variety-hybrid-type-select"
                />
                <Checkbox
                    disabled={false}
                    label={formatMessage(messages.displayTraits)}
                    onChange={(e, value) => onInputChange(model.PROPS_DISPLAY_TRAITS, value)}
                    value={reportData[model.PROPS_DISPLAY_TRAITS]}
                    testId="sky-planting-summary-display-traits-checkbox"
                />
                <Checkbox
                    disabled={false}
                    label={formatMessage(messages.displayFieldAcres)}
                    onChange={(e, value) => onInputChange(model.PROPS_DISPLAY_FIELD_ACRES, value)}
                    value={reportData[model.PROPS_DISPLAY_FIELD_ACRES]}
                    testId="sky-planting-summary-display-field-acres-checkbox"
                />
                <Checkbox
                    disabled={false}
                    label={formatMessage(messages.summary)}
                    onChange={(e, value) => onInputChange(model.PROPS_PLANTING_SUMMARY, value)}
                    value={reportData[model.PROPS_PLANTING_SUMMARY]}
                    testId="sky-planting-summary-summary-checkbox"
                />
            </SubSection>
        </Section>
    );
}

SkyPlantingSummary.propTypes = {
    intl: intlShape,
    reportPreferences: PropTypes.object,
    selectedCrop: null,
};

SkyPlantingSummary.defaultReportOptions = {
    [model.PROPS_CROP_GUID]: null,
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_VARIETY_HYBRID_TYPE]: "variety",
    [model.PROPS_START_DATE]: null,
    [model.PROPS_END_DATE]: null,
    [model.PROPS_CROPPING_SEASON_GUID]: [],
    [model.PROPS_DISPLAY_TRAITS]: false,
    [model.PROPS_DISPLAY_FIELD_ACRES]: true,
    [model.PROPS_PLANTING_SUMMARY]: true,
};

SkyPlantingSummary.reportPreferences = [
    model.PROPS_DISPLAY_TRAITS,
    model.PROPS_DISPLAY_FIELD_ACRES,
    model.PROPS_PLANTING_SUMMARY,
    model.PROPS_VARIETY_HYBRID_TYPE,
];

SkyPlantingSummary.reportName = ReportAPI.ReportNames.AGVANCE_SKY_PLANTING_SUMMARY;

export default injectIntl(SkyPlantingSummary) as ReportOptionsComponent<
    ReportOptionsComponentProps & SkyPlantingSummaryProps
>;
